import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import {
  deleteKnowledgeFile,
  type ProjectsApiOptions,
} from '../../api/projects';
import { type ContextFileEntry } from './types';

const useStyles = makeStyles((theme: Theme) => ({
  warningText: {
    color: theme.palette.warning.dark,
    marginTop: theme.spacing(1),
  },
  errorText: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
  },
}));

type Props = {
  file: ContextFileEntry | null;
  projectId: string;
  apiOptions: ProjectsApiOptions;
  isPreviewMode: boolean;
  onClose: () => void;
  onDeleted: (fileId: string) => void;
};

export function DeleteContextFileDialog({
  file,
  projectId,
  apiOptions,
  isPreviewMode,
  onClose,
  onDeleted,
}: Props) {
  const classes = useStyles();
  const { t } = useTranslation('projects');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const handleClose = () => {
    if (submitting) return;
    setError(undefined);
    onClose();
  };

  const handleConfirm = async () => {
    if (!file) return;
    setSubmitting(true);
    setError(undefined);
    try {
      if (!isPreviewMode) {
        await deleteKnowledgeFile(apiOptions, projectId, file.fileId);
      }
      onDeleted(file.fileId);
    } catch {
      setError(t('contextFiles.deleteError'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={!!file}
      onClose={handleClose}
      aria-labelledby="delete-context-file-dialog-title"
      data-testid="delete-context-file-dialog"
    >
      <DialogTitle id="delete-context-file-dialog-title">
        {t('contextFiles.deleteConfirmTitle')}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {t('contextFiles.deleteConfirmDescription')}
        </DialogContentText>
        {file?.isIncluded && (
          <Typography variant="body2" className={classes.warningText}>
            {t('contextFiles.deleteConfirmIncludedWarning')}
          </Typography>
        )}
        {error && (
          <Typography variant="body2" className={classes.errorText}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          {t('actions.cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={submitting}
          variant="contained"
          color="secondary"
          data-testid="delete-context-file-confirm-button"
        >
          {t('contextFiles.deleteFile')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
